import React, { useState, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Box, Typography, Alert } from '@mui/material'
import { useConfirmGuestAppointment } from '@/hooks'
import { useTenantContext } from '@/context'
import { AppointmentConfirmedDialog } from '@/components/donation'
import { CommonTextField, CommonButton, CommonPhone } from '@/components/common'
import {
  formatDisplayTime,
  formatIsoDateWithWeekday,
  extractValidationErrors,
  parseGuestConfirmAppointmentParams,
  trackDailyStoryConversion,
} from '@/utils'
import { localStorageService } from '@/services'
import { createGuestAppointmentSchema, STORAGE_KEYS } from '@/constants'

export const GuestConfirmAppointmentPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { tenant } = useTenantContext()
  const { confirmGuestAppointment, loadingConfirmGuestAppointment } =
    useConfirmGuestAppointment()

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
  })
  const [formErrors, setFormErrors] = useState({})
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false)
  const [bookingError, setBookingError] = useState(null)

  const {
    slot_id: slotId,
    date,
    start_time,
    operation_id,
    operation_type,
  } = parseGuestConfirmAppointmentParams(new URLSearchParams(location.search))

  const donorName = useMemo(() => {
    return formData.firstName + ' ' + formData.lastName || ''
  }, [formData.firstName, formData.lastName])

  // Check tenant configuration for required fields
  const isEmailRequired = useMemo(() => {
    const emailConfig = tenant?.configuration_options?.find(
      item => item.code === 'DP_GUEST_EMAIL_REQUIRED'
    )
    return emailConfig?.value === 'true'
  }, [tenant])

  const isPhoneRequired = useMemo(() => {
    const phoneConfig = tenant?.configuration_options?.find(
      item => item.code === 'DP_GUEST_PHONE_REQUIRED'
    )
    return phoneConfig?.value === 'true'
  }, [tenant])

  const isEmailOrPhoneRequired = useMemo(() => {
    const emailOrPhoneConfig = tenant?.configuration_options?.find(
      item => item.code === 'DP_GUEST_EMAIL_OR_PHONE_REQUIRED'
    )
    return emailOrPhoneConfig?.value === 'true'
  }, [tenant])

  // Create validation schema based on tenant configuration
  const validationSchema = useMemo(() => {
    return createGuestAppointmentSchema(
      isEmailRequired,
      isPhoneRequired,
      isEmailOrPhoneRequired
    )
  }, [isEmailRequired, isPhoneRequired, isEmailOrPhoneRequired])

  const handleConfirmAppointment = async e => {
    if (e) e.preventDefault()
    if (!slotId) return

    setFormErrors({})
    setBookingError(null)

    try {
      await validationSchema.validate(formData, { abortEarly: false })
      const response = await confirmGuestAppointment({
        slot_id: slotId,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone_number: formData.phoneNumber,
        operation_id: operation_id || undefined,
        operation_type: operation_type || undefined,
      })

      if (response?.status_code >= 400) {
        setBookingError(
          response?.response ||
            'This appointment could not be booked. Please choose another time.'
        )
        return
      }

      if (response && response.data) {
        trackDailyStoryConversion()
        setOpenSuccessDialog(true)
        localStorageService.removeItem(STORAGE_KEYS.IS_GUEST)
        localStorageService.removeItem(STORAGE_KEYS.IS_GUEST_AT)
      }
    } catch (error) {
      const fieldErrors = extractValidationErrors(error)
      if (fieldErrors) {
        setFormErrors(fieldErrors)
        return
      }
      console.error('Error confirming appointment:', error)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        component='form'
        onSubmit={handleConfirmAppointment}
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          maxWidth: 700,
          gap: 3,
        }}
      >
        <Typography
          variant='h4'
          sx={{
            textAlign: 'center',
            mb: 2,
          }}
        >
          Give us a little bit of information so we can book your appointment.
        </Typography>

        {bookingError && (
          <Alert severity='error' sx={{ width: '100%' }}>
            {bookingError}
          </Alert>
        )}

        <CommonTextField
          id='first-name'
          label='First Name'
          value={formData.firstName}
          setValue={value => setFormData({ ...formData, firstName: value })}
          type='text'
          required={true}
          error={formErrors.firstName}
          placeholder='Enter your first name'
          inputProps={{
            name: 'firstName',
            autoComplete: 'given-name',
          }}
        />

        <CommonTextField
          id='last-name'
          label='Last Name'
          value={formData.lastName}
          setValue={value => setFormData({ ...formData, lastName: value })}
          type='text'
          required={true}
          error={formErrors.lastName}
          placeholder='Enter your last name'
          inputProps={{
            name: 'lastName',
            autoComplete: 'family-name',
          }}
        />

        <CommonTextField
          id='email'
          label='Email'
          value={formData.email}
          setValue={value => setFormData({ ...formData, email: value })}
          type='email'
          error={formErrors.email}
          placeholder={
            isEmailRequired
              ? 'Enter your email'
              : isEmailOrPhoneRequired
                ? 'Enter your email (email or phone required)'
                : 'Enter your email (optional)'
          }
          required={isEmailRequired}
          inputProps={{
            name: 'email',
            autoComplete: 'username email',
          }}
        />

        <CommonPhone
          id='phone-number'
          label='Phone Number'
          value={formData.phoneNumber}
          setValue={value => setFormData({ ...formData, phoneNumber: value })}
          mask='(999) 999-9999'
          required={isPhoneRequired}
          error={formErrors.phoneNumber}
          placeholder={
            isPhoneRequired
              ? '(###)-###-####'
              : isEmailOrPhoneRequired
                ? '(###)-###-#### (email or phone required)'
                : '(###)-###-#### (optional)'
          }
          inputProps={{
            name: 'phoneNumber',
          }}
        />

        {isEmailOrPhoneRequired && !isEmailRequired && !isPhoneRequired && (
          <Typography
            variant='body2'
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
              mt: -2,
              mb: -1,
              textAlign: 'center',
            }}
          >
            Please provide either an email address or phone number
          </Typography>
        )}

        <CommonButton
          id='confirm-appointment-button'
          label='Book Appointment'
          type='submit'
          onClick={handleConfirmAppointment}
          loading={loadingConfirmGuestAppointment}
          size='large'
          style={{ width: '100%' }}
        />

        <AppointmentConfirmedDialog
          open={openSuccessDialog}
          name={donorName}
          date={formatIsoDateWithWeekday(date)}
          time={start_time ? formatDisplayTime(start_time, 'h:mm a') : ''}
          onClose={() => navigate(-1)}
        />
      </Box>
    </Box>
  )
}
